// src/pages/team/TeamProjectsTab.tsx

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { listLinksByDirection } from '@/features/resources/api';
import {
  Button,
  Card,
  CardContent,
  Badge,
} from '@react/components';
import { getEntityId } from '@/features/team/logic';

// ---------------------------------------------------------------------------
// Project card
// ---------------------------------------------------------------------------

function ProjectCard({
  project,
  onClick,
  t,
}: {
  project: any;
  onClick: () => void;
  t: (key: string) => string;
}) {
  const name = project.name || project.title || t('common.untitled');
  const description = project.description || '';
  const memberCount = project.members_count ?? project.member_count ?? 0;
  const taskTotal = project.task_count ?? project.tasks_total ?? 0;
  const taskDone = project.task_done_count ?? project.tasks_done ?? 0;
  const progress = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-ring"
      aria-label={name}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0 rounded-lg bg-primary/10 p-2">
            <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-foreground truncate">{name}</h3>
        </div>
      </div>

      {/* Description */}
      {description && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{description}</p>
      )}

      {/* Progress bar */}
      {taskTotal > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t('team.project.progress')}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {memberCount > 0 && (
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
            {memberCount}
          </span>
        )}
        {taskTotal > 0 && (
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {taskDone}/{taskTotal}
          </span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function ProjectsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border p-5 animate-pulse">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-muted" />
            <div className="h-4 w-32 rounded bg-muted" />
          </div>
          <div className="h-3 w-full rounded bg-muted mb-2" />
          <div className="h-3 w-2/3 rounded bg-muted mb-4" />
          <div className="h-1.5 rounded-full bg-muted mb-3" />
          <div className="flex gap-3">
            <div className="h-3 w-12 rounded bg-muted" />
            <div className="h-3 w-12 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TeamProjectsTabProps {
  teamId: string;
}

export const TeamProjectsTab = observer(({ teamId }: TeamProjectsTabProps) => {
  const store = useProject();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [linkedIds, setLinkedIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!teamId) return;
    // STANDARD view: BASIC strips `parent`, which the scoping below reads.
    // parent_id/parent_type land as query params the BFF now maps into the
    // request (proto query_mapping), so the list is team-scoped server-side.
    store.ListProjects({
      pagination: { cursor: '', limit: 100, direction: 'forward' as const, view: 2 },
      sort: [],
      view: 2,
      filters: { parent_id: teamId, parent_type: 'team' } as any,
    } as any);
    // The team's projects are the ones linked team→project (the Project
    // contract has no parent field yet, so linkage lives in resource links).
    listLinksByDirection('team', teamId, 'LINK_DIRECTION_BOTH', false)
      .then((res) => {
        const ids = new Set<string>();
        for (const link of res.links?.items ?? []) {
          if (link.source?.type_name === 'project' && link.source.id) ids.add(link.source.id);
          if (link.target?.type_name === 'project' && link.target.id) ids.add(link.target.id);
        }
        setLinkedIds(ids);
      })
      .catch(() => setLinkedIds(new Set()));
  }, [teamId, store]);

  // Scope to this team: linked projects (today's mechanism) plus parent refs
  // (once the contract grows one). Never show the whole product's projects.
  const projects = useMemo(
    () =>
      (store.items ?? []).filter(
        (p: any) => p.parent?.id === teamId || (linkedIds?.has(p.entity?.id ?? '') ?? false),
      ),
    [store.items, teamId, linkedIds],
  );

  const handleProjectClick = useCallback(
    (projectId: string) => navigate(`/projects/${projectId}`),
    [navigate],
  );

  const handleCreateProject = useCallback(
    () => navigate(`/projects/new?team_id=${teamId}`),
    [navigate, teamId],
  );

  if (store.isLoading && projects.length === 0) {
    return <ProjectsSkeleton />;
  }

  if (!store.isLoading && projects.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-foreground mb-1">{t('team.project.empty')}</p>
          <p className="text-xs text-muted-foreground mb-4">{t('team.project.emptyDescription')}</p>
          <Button size="sm" onClick={handleCreateProject}>
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {t('team.project.create')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" size="sm">{projects.length}</Badge>
          <span className="text-sm text-muted-foreground">{t('team.tab.projects')}</span>
        </div>
        <Button size="sm" onClick={handleCreateProject}>
          <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t('team.project.create')}
        </Button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map((project: any) => {
          const projectId = getEntityId(project);
          return (
            <ProjectCard
              key={projectId || Math.random()}
              project={project}
              onClick={() => handleProjectClick(projectId)}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
});

export default TeamProjectsTab;
